import { X } from "@phosphor-icons/react";
import useAllLabels from "features/labels/datahooks/useAllLabels";
import { useEffect } from "react";

import { BooleanFilter, SelectionTags } from "common/components/Filter";
import SearchInputFilter from "common/components/Filter/SearchInputFilter";
import LabelTag from "common/components/LabelTag";
import LinkedInAccountsTag from "common/components/LinkedInAccountsTag";
import { Accordion } from "common/components/ui/Accordion";
import { Tag } from "common/components/ui/Tag";
import { LEADS_FACETS } from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import useLeadsSearchParams from "common/hooks/useLeadsSearchParams";
import type { LeadsSearchParams, SelectedFacetParam } from "common/types";

import { LEAD_FACET_KEYS } from "../../constants";
import SearchFacet from "./SearchFacet";

export default function SearchFilters({
  setSearchParams,
  searchParams,
}: {
  searchParams: LeadsSearchParams<SelectedFacetParam>;
  setSearchParams: (
    searchParams: Partial<LeadsSearchParams<SelectedFacetParam>>,
  ) => void;
}) {
  const { allLabels } = useAllLabels("workspace");
  const { toggleSearchParam, setSearchParam } = useLeadsSearchParams(
    searchParams,
    setSearchParams,
  );

  useEffect(() => {
    setSearchParams(searchParams);
  }, [searchParams]);

  return (
    <>
      <BooleanFilter
        label="Favorite"
        isChecked={searchParams.favorite}
        isDisabled={false}
        onCheckedChange={(isChecked) => setSearchParam("favorite", isChecked)}
      />
      <Accordion
        type="multiple"
        // tests don't work because all the accordions are closed by default. This opens all the accordions in test environment.
        defaultValue={
          import.meta.env.MODE === "test"
            ? typedObjectEntries(LEADS_FACETS).map(([, { label }]) => label)
            : [LEADS_FACETS.lead_of.label]
        }
      >
        {LEAD_FACET_KEYS.map((facetKey) => {
          const { label: facetLabel, icon } = LEADS_FACETS[facetKey];
          const searchParamsKey =
            facetKey === "schools" ? "education" : facetKey;
          const selectedFacets = searchParams[searchParamsKey];

          return (
            <SearchInputFilter
              key={facetKey}
              label={facetLabel}
              icon={icon}
              renderTagsComponent={(resetInputValue) => {
                if (selectedFacets.length) {
                  if (facetKey === "labels") {
                    return (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 px-2">
                        {selectedFacets.map((facet) => {
                          const label = allLabels.find(
                            ({ id }) => id === facet.id,
                          );
                          if (label) {
                            return (
                              <LabelTag
                                key={label.id}
                                size="lg"
                                label={label}
                                onRemove={() =>
                                  toggleSearchParam("labels", facet)
                                }
                              />
                            );
                          }
                          return (
                            <Tag
                              key={facet.id}
                              size="lg"
                              variant="tertiary"
                              rightIcon={
                                <button
                                  aria-label={`remove label parameter ${facet.name}`}
                                  type="button"
                                  onClick={() =>
                                    toggleSearchParam("labels", facet)
                                  }
                                >
                                  <X />
                                </button>
                              }
                            >
                              {facet.name}
                            </Tag>
                          );
                        })}
                      </div>
                    );
                  }
                  if (facetKey === "lead_of") {
                    return (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 px-2">
                        {selectedFacets.map((param) => (
                          <LinkedInAccountsTag
                            key={param.name}
                            accountIds={[param.name]}
                            size="lg"
                            rightComponent={
                              <button
                                type="button"
                                aria-label={`remove account filter ${param.name}`}
                                onClick={() =>
                                  toggleSearchParam("lead_of", param)
                                }
                              >
                                <X size={16} className="fill-black-950" />
                              </button>
                            }
                          />
                        ))}
                      </div>
                    );
                  }

                  return (
                    <SelectionTags
                      selection={selectedFacets}
                      labelKey="name"
                      onRemoveFilter={(value) => {
                        toggleSearchParam(searchParamsKey, value);
                        resetInputValue();
                      }}
                    />
                  );
                }
              }}
            >
              {(query) => (
                <SearchFacet
                  facetKey={facetKey}
                  searchParams={searchParams}
                  searchValue={query}
                  selectedFacets={selectedFacets}
                  toggleSearchParam={(param) =>
                    toggleSearchParam(searchParamsKey, param)
                  }
                />
              )}
            </SearchInputFilter>
          );
        })}
      </Accordion>
    </>
  );
}
